// Plain-word vocabularies for the ski v3 UI (owner copy). No runtime imports, so light shared
// components (the confidence badge, the elevation panel) can use them without pulling in the
// label engine. British English, no em dashes.
import type { StoredSkiBand, StoredTimeBlock } from "@/lib/conditions/ski-v3-carriers";
import type { ConfidenceClass } from "@/lib/conditions/ski/types";

export type SkiCoverKey = StoredSkiBand["cov"];
export type SkiSurfaceClass = StoredTimeBlock[2];

/** Cover words (owner copy). A closure is carried by the band status, so "closed" reads as bare. */
export const SKI_COVER_WORDS: Readonly<Record<SkiCoverKey, string>> = {
  full: "Full cover",
  thin: "Thin in places",
  marginal: "Marginal",
  patchy: "Patchy (grass showing)",
  ribbon: "Ribbon only",
  bare: "Bare or closed",
  closed: "Bare or closed",
};

/** Surface classes in plain words ("hard" reads as firm, "chop" as choppy). */
export const SKI_SURFACE_WORDS: Readonly<Record<SkiSurfaceClass, string>> = {
  powder: "powder",
  groomed: "groomed",
  hard: "firm",
  corn: "corn",
  slush: "slush",
  chop: "choppy",
};

/** Confidence classes as plain words. */
export const SKI_CONFIDENCE_WORDS: Readonly<Record<ConfidenceClass, string>> = {
  A: "Checked against reports",
  B: "Partly checked",
  C: "Estimated from weather models",
};

export type SkiV3Flag = "cover-risk" | "ice-risk" | "cover-assumed" | "limited-terrain";

/** Flags shown as chips. Limited terrain has its own line under the piste headline. */
export const SKI_FLAG_CHIPS: Readonly<Partial<Record<SkiV3Flag, string>>> = {
  "cover-risk": "Thin cover risk",
  "ice-risk": "Ice risk",
  "cover-assumed": "Cover assumed",
};

export const FLAG_ORDER: readonly SkiV3Flag[] = ["cover-risk", "ice-risk", "cover-assumed", "limited-terrain"];

export function coverWord(cov: string | null | undefined): string | null {
  return cov && Object.prototype.hasOwnProperty.call(SKI_COVER_WORDS, cov) ? SKI_COVER_WORDS[cov as SkiCoverKey] : null;
}

export function surfaceWord(cls: string | null | undefined): string | null {
  return cls && Object.prototype.hasOwnProperty.call(SKI_SURFACE_WORDS, cls) ? SKI_SURFACE_WORDS[cls as SkiSurfaceClass] : null;
}

export function confidenceClassWord(cls: string | null | undefined): string | null {
  return cls === "A" || cls === "B" || cls === "C" ? SKI_CONFIDENCE_WORDS[cls] : null;
}

export function isInt100(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= 100;
}

/** "Likely 62 to 78" from a P10 to P90 range on the 0 to 100 scale; null when incomplete. */
export function likelyRangeText(lo: number | null | undefined, hi: number | null | undefined): string | null {
  if (!isInt100(lo) || !isInt100(hi)) return null;
  return `Likely ${Math.min(lo, hi)} to ${Math.max(lo, hi)}`;
}

/** Chip labels for the flags, in a fixed order, without limited terrain. */
export function flagChipLabels(flags: readonly string[] | null | undefined): string[] {
  if (!flags?.length) return [];
  const set = new Set(flags);
  return FLAG_ORDER.flatMap((flag) => (set.has(flag) && SKI_FLAG_CHIPS[flag] ? [SKI_FLAG_CHIPS[flag] as string] : []));
}

